import { useMemo } from "react";
import { getActiveConfig, getActiveDemoInfo } from "../index";
import { useRuntimeConfigSafe } from "../../context/RuntimeConfig.Context";

/**
 * Custom hook to access the active configuration
 * based on the current route (instead of URL parameters)
 *
 * Priority:
 * 1. Runtime JSON config (if available and loaded)
 * 2. TypeScript config (fallback for development)
 */
export function useConfig() {
  // Get runtime JSON config (returns null if provider not available)
  const runtimeConfigContext = useRuntimeConfigSafe();

  // Fallback to TypeScript config
  const tsConfig = useMemo(() => {
    return getActiveConfig();
  }, []);

  const demoInfo = useMemo(() => {
    return getActiveDemoInfo();
  }, []);

  // Use runtime config if available and loaded, otherwise use TS config
  const config = runtimeConfigContext?.config || tsConfig;

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      config,
      demoInfo,
      isDemoMode: demoInfo.id !== "default",
      // Expose runtime config state for debugging
      runtimeLoading: runtimeConfigContext?.loading ?? false,
      runtimeError: runtimeConfigContext?.error ?? null,
      isUsingJsonConfig: !!runtimeConfigContext?.config,
      updateConfig: runtimeConfigContext?.updateConfig ?? (() => {}),
    }),
    [
      config,
      demoInfo,
      runtimeConfigContext?.loading,
      runtimeConfigContext?.error,
      runtimeConfigContext?.config,
      runtimeConfigContext?.updateConfig,
    ]
  );
}

/**
 * Hook to access a specific configuration property
 */
export function useConfigValue<
  T extends keyof ReturnType<typeof getActiveConfig>
>(key: T): ReturnType<typeof getActiveConfig>[T] {
  const { config } = useConfig();
  return config[key];
}

export default useConfig;
